import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type Skill, type SkillsCatalog, type DeployResult } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardBody } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { UploadCloud, AlertCircle, CheckCircle2 } from 'lucide-react';

export function SkillsPage() {
  const qc = useQueryClient();
  const catalog = useQuery({
    queryKey: ['skills'],
    queryFn: () => api.get<SkillsCatalog>('/api/skills'),
  });

  const [deployResult, setDeployResult] = useState<DeployResult | null>(null);
  const [deployError, setDeployError] = useState<string | null>(null);

  const deploy = useMutation({
    mutationFn: () => api.post<DeployResult>('/api/skills/deploy'),
    onSuccess: (data) => {
      setDeployResult(data);
      setDeployError(null);
      qc.invalidateQueries({ queryKey: ['skills'] });
    },
    onError: (err: Error) => {
      setDeployError(err.message);
      setDeployResult(null);
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Skills</h1>
          <p className="text-sm text-slate-500 mt-1">
            Catalog is owned by the{' '}
            <code className="font-mono text-xs">godmode-skills</code> git repo. Pushes to{' '}
            <code className="font-mono text-xs">main</code> trigger an automatic deploy via webhook;
            use the button to re-deploy manually.
          </p>
        </div>
        <Button onClick={() => deploy.mutate()} disabled={deploy.isPending} className="shrink-0">
          <UploadCloud size={14} />
          {deploy.isPending ? 'Deploying…' : 'Sync from git & deploy'}
        </Button>
      </div>

      {deployError && (
        <Card>
          <CardBody className="text-sm text-red-700 bg-red-50 flex items-start gap-2">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>{deployError}</span>
          </CardBody>
        </Card>
      )}

      {deployResult && <DeploySummaryCard result={deployResult} />}

      <CatalogCard catalog={catalog.data} loading={catalog.isLoading} />
    </div>
  );
}

function CatalogCard({
  catalog,
  loading,
}: {
  catalog: SkillsCatalog | undefined;
  loading: boolean;
}) {
  if (loading) {
    return (
      <Card>
        <CardBody className="p-5 text-sm text-slate-500">Loading…</CardBody>
      </Card>
    );
  }
  if (!catalog) {
    return (
      <Card>
        <CardBody className="p-5 text-sm text-slate-500">No data.</CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardBody className="p-0">
        <div className="px-5 py-3 border-b border-slate-200 text-xs text-slate-500 flex items-center gap-3 flex-wrap">
          <span>
            Source:{' '}
            <code className="font-mono text-[11px] text-slate-700">{catalog.source_dir}</code>
          </span>
          {catalog.source_present ? (
            <Badge tone="success">checkout present</Badge>
          ) : (
            <Badge tone="warning">no local checkout</Badge>
          )}
          {catalog.error && <span className="text-red-600">{catalog.error}</span>}
        </div>

        {!catalog.source_present ? (
          <div className="p-8 text-center text-sm text-slate-500 space-y-2">
            <div>
              No <code className="font-mono text-xs">.skills-source</code> at the configured path on
              this host. The catalog list is read locally for the UI; deploys still work fine
              against your orchestrator servers regardless.
            </div>
            <div className="text-xs">
              To populate this view locally:{' '}
              <code className="font-mono text-xs">
                git clone &lt;godmode-skills repo&gt; {catalog.source_dir}
              </code>
            </div>
          </div>
        ) : catalog.skills.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">
            Source directory is empty. Push skills to the godmode-skills repo, then run a deploy.
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                <th className="px-5 py-3">Name</th>
                <th className="px-3 py-3">Description</th>
                <th className="px-3 py-3">Auto-deploy</th>
                <th className="px-3 py-3 text-right">Files</th>
              </tr>
            </thead>
            <tbody>
              {catalog.skills.map((s) => (
                <SkillRow key={s.name} skill={s} />
              ))}
            </tbody>
          </table>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function SkillRow({ skill }: { skill: Skill }) {
  return (
    <tr className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
      <td className="px-5 py-3 font-mono text-xs text-slate-900">{skill.name}</td>
      <td className="px-3 py-3 text-slate-700">
        {skill.description ? (
          <span className="line-clamp-2">{skill.description}</span>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      <td className="px-3 py-3">
        {skill.auto_deploy ? (
          <Badge tone="success">on</Badge>
        ) : (
          <span className="text-xs text-slate-400">off</span>
        )}
      </td>
      <td className="px-3 py-3 text-right text-xs text-slate-500 font-mono">{skill.file_count}</td>
    </tr>
  );
}

function DeploySummaryCard({ result }: { result: DeployResult }) {
  const allOk = result.failed === 0 && result.deployments_failed === 0;
  return (
    <Card>
      <CardBody className="text-sm space-y-3">
        <div className="flex items-center gap-2">
          {allOk ? (
            <CheckCircle2 size={16} className="text-emerald-600" />
          ) : (
            <AlertCircle size={16} className="text-red-600" />
          )}
          <span>
            <b>{result.ok}/{result.attempted}</b> server{result.attempted === 1 ? '' : 's'} updated
            ·{' '}
            <b>{result.deployments_ok}</b> tenant{result.deployments_ok === 1 ? '' : 's'} redeployed
            {result.deployments_failed > 0 && (
              <span className="text-red-600">, {result.deployments_failed} failed</span>
            )}
          </span>
        </div>

        {!result.catalog_refreshed && (
          <div className="text-xs text-amber-600">
            CP catalog not refreshed
            {result.catalog_error ? ` (${result.catalog_error})` : ''} — the list below may be
            stale; the fleet deploy still ran.
          </div>
        )}

        {result.results.map((r) => (
          <div key={r.server_id} className="text-xs space-y-1 border-l-2 border-slate-200 pl-3">
            <div className="font-mono text-slate-700">
              {r.server_id}{' '}
              {r.git_pulled ? (
                <Badge tone="success">git ok</Badge>
              ) : (
                <Badge tone="danger">git failed</Badge>
              )}
            </div>
            {r.git_error && (
              <div className="text-red-600 font-mono text-[11px]">{r.git_error}</div>
            )}
            {r.error && (
              <div className="text-red-600 font-mono text-[11px]">{r.error}</div>
            )}
            {r.deployments.length === 0 ? (
              <div className="text-slate-500">no tenant containers running on this server</div>
            ) : (
              <ul className="space-y-0.5">
                {r.deployments.map((d) => (
                  <li key={d.container} className="font-mono text-[11px] flex items-start gap-2">
                    {d.ok ? (
                      <span className="text-emerald-600 shrink-0">✓</span>
                    ) : (
                      <span className="text-red-600 shrink-0">✗</span>
                    )}
                    <span className={d.ok ? 'text-slate-700' : 'text-red-700'}>
                      {d.container}
                      {d.error ? ` — ${d.error}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </CardBody>
    </Card>
  );
}
